import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import {
  ContactType,
  getContactDetailsStandardFields,
  getContactDetailsType,
  isContactDetailsGroup,
} from "./roles";
import { MatterField } from "../types";

export type ContactFieldCandidate = {
  layout: { id: string };
  fieldName: string;
  label: string;
};

type MappedFieldLike = {
  values: {
    groupUri?: string | null;
    layout?: { id: string } | null;
    field?: { name: string } | null;
  };
};

type ContactGroupLike = {
  uri: string;
  values: {
    type?: string | null;
    layout?: { id: string } | null;
    field?: { name?: string | null } | null;
  };
};

// NUL never appears in a layout id or field name
const keySeparator = "\u0000";

const toMappingKey = (layoutId: string, fieldName: string) =>
  `${layoutId}${keySeparator}${fieldName}`;

const isExcluded = (
  uri: string | null | undefined,
  excludeGroupUri: string | null | undefined,
) => !!excludeGroupUri && uri === excludeGroupUri;

export const findDuplicateContactFields = ({
  candidates,
  fields,
  excludeGroupUri,
}: {
  candidates: ContactFieldCandidate[];
  fields: MappedFieldLike[];
  excludeGroupUri?: string | null;
}): ContactFieldCandidate[] => {
  if (!candidates.length) return [];

  const mappedKeys = new Set<string>();
  fields.forEach(({ values }) => {
    if (isExcluded(values.groupUri, excludeGroupUri)) return;
    if (!values.layout || !values.field) return;
    mappedKeys.add(toMappingKey(values.layout.id, values.field.name));
  });
  if (!mappedKeys.size) return [];

  return candidates.filter((candidate) =>
    mappedKeys.has(toMappingKey(candidate.layout.id, candidate.fieldName)),
  );
};

// a role's contact details belong in one place, so anything already mapped to this role and contact type counts, whatever the field is called
export const findRoleContactFields = <T extends MappedFieldLike>({
  fields,
  layoutId,
  contactType,
  excludeGroupUri,
}: {
  fields: T[];
  layoutId: string | undefined;
  contactType: ContactType;
  excludeGroupUri?: string | null;
}): T[] => {
  if (!layoutId) return [];

  return fields.filter(
    ({ values }) =>
      !isExcluded(values.groupUri, excludeGroupUri) &&
      values.layout?.id === layoutId &&
      getContactDetailsType(values.field) === contactType,
  );
};

export const resolveStandardContactFields = ({
  matterFields,
  contactType,
}: {
  matterFields: MatterField[];
  contactType: ContactType;
}): MatterField[] =>
  getContactDetailsStandardFields(contactType)
    .map((name) => matterFields.find((field) => field.name === name))
    .filter(isNotNullOrUndefined);

// emptied groups still count, we match on existence not contents
export const findExistingContactDetailsGroup = <T extends ContactGroupLike>({
  groups,
  layoutId,
  contactType,
  excludeGroupUri,
}: {
  groups: T[];
  layoutId: string | undefined;
  contactType: ContactType;
  excludeGroupUri?: string | null;
}): T | undefined => {
  if (!layoutId) return undefined;

  return groups.find(
    (group) =>
      !isExcluded(group.uri, excludeGroupUri) &&
      isContactDetailsGroup(group.values) &&
      group.values.layout?.id === layoutId &&
      getContactDetailsType(group.values.field) === contactType,
  );
};

export const hasExistingContactDetailsGroup = (args: {
  groups: ContactGroupLike[];
  layoutId: string | undefined;
  contactType: ContactType;
  excludeGroupUri?: string | null;
}): boolean => !!findExistingContactDetailsGroup(args);
